import { defineSlotRecipe } from '@chakra-ui/react';
import { inputTheme, sharedFieldBase, sizes } from './Input';

/*
 * v3 splits `Input` and `NumberInput` into separate slot recipes, even
 * though this design system uses the exact same visual look for both.
 * Chakra's own built-in `numberInput` recipe derives its `input` slot's
 * `size`/`variant` styles from Chakra's *stock* `input` recipe at
 * module-load time — confirmed via its actual source
 * (`@chakra-ui/react/dist/esm/theme/recipes/number-input.js`:
 * `variant: mapEntries(inputRecipe.variants.variant, ...)`, importing
 * chakra's own bundled `./input.js`, not this app's customized one). That
 * derivation runs before this app's `theme.ts` config is merged in, so it
 * never picks up this app's `Input.ts` customizations at all.
 *
 * Confirmed empirically (`system.getSlotRecipeFn('numberInput')`) that
 * every real `NumberInput` in the app — quantities, prices, discounts, rate
 * config, anywhere using `MCustomNumberInput`/`MAdditionNumberInput` — was
 * silently rendering with Chakra's generic default input chrome (no app
 * border color, background, focus halo, disabled treatment, or even the
 * app's own height/padding) instead of the design system's actual look,
 * for every variant including the default `outline`.
 *
 * Fix mirrors the same technique Chakra's own file uses, but points at this
 * app's `inputTheme`/`sizes` (from `./Input.ts`) instead of Chakra's stock
 * ones, so every `size`/`variant` combination renders identically to a real
 * `Input` with the same props (verified empirically, property-by-property,
 * against `system.getRecipeFn('input')`'s output for every variant/size
 * combination — one exception below). The structural root/control/trigger/
 * valueText styles and stepper layout are untouched, inherited from
 * Chakra's built-in recipe via the standard per-key config merge — `slots`
 * is restated in full (not partial) since Panda replaces rather than
 * merges array-valued config keys. That includes `Input.ts`'s
 * `focusVisibleRing: 'none'`, which clears the stock variant's near-black
 * `:focus-visible` outline here too.
 */
export const numberInputTheme = defineSlotRecipe({
  slots: [
    'root',
    'label',
    'input',
    'control',
    'valueText',
    'incrementTrigger',
    'decrementTrigger',
    'scrubber',
  ],
  base: {
    // Same base as `Input`'s recipe. Besides restoring `_readOnly`'s
    // `cursor: 'text'` (variants like `quantity` replace that block), it fixes
    // the condition order: base keys come first in the merged style, so a
    // base holding only `_readOnly` emitted the variant's `_readOnly` rule
    // before `_focus`/`_focusVisible`. All are `!important` at equal
    // specificity, so the later focus halo beat `readonly`/`quantity`'s
    // `boxShadow: 'none !important'` on a focused read-only quantity.
    input: sharedFieldBase,
  },
  variants: {
    size: Object.fromEntries(
      Object.entries(sizes).map(([key, styles]) => [key, { input: styles }]),
    ),
    variant: Object.fromEntries(
      Object.entries(inputTheme.variants?.variant ?? {}).map(
        ([key, styles]) => [key, { input: styles }],
      ),
    ),
  },
  defaultVariants: {
    size: 'sm',
    variant: 'outline',
  },
});

export default numberInputTheme;
